import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calculator, 
  Search, 
  ShieldCheck, 
  Clock, 
  BookOpen, 
  Layers, 
  HardHat, 
  ChevronLeft, 
  Sparkles,
  CheckCircle2,
  FileCheck
} from 'lucide-react';
import { SEO } from '../../components/layout/SEO';

export const LandingPage = () => {
  const tools = [
    {
      id: 'civil',
      title: 'محاسبات و متره کارگاهی',
      slug: '/civil',
      category: 'ابزار محاسباتی',
      desc: 'محاسبه سریع وزن میلگرد، حجم بتن‌ریزی، کنترل درصد تراکم خاک و آسفالت بر اساس استاندارد کارگاه.',
      icon: Calculator,
      theme: 'text-amber-400 bg-amber-500/10 border-amber-500/20'
    },
    {
      id: 'vision',
      title: 'پایش و عارضه‌یابی ترک بتن',
      slug: '/vision',
      category: 'هوش مصنوعی سازه',
      desc: 'آنالیز تصویری ترک‌های سازه‌ای و غیرسازه‌ای جهت مستندسازی فنی در بازدیدهای دوره‌ای نظارت.',
      icon: Search,
      theme: 'text-sky-400 bg-sky-500/10 border-sky-500/20'
    },
    {
      id: 'vault',
      title: 'بایگانی امن اسناد و نقشه‌ها',
      slug: '/vault',
      category: 'آرشیو فنی',
      desc: 'ذخیره مطمئن و رمزنگاری‌شده صورت‌جلسات، شیت آزمایش‌های مکانیک خاک و نقشه‌های اجرایی در مرورگر.',
      icon: ShieldCheck,
      theme: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20'
    },
    {
      id: 'journal',
      title: 'گزارش روزانه کارگاه و وقایع',
      slug: '/journal',
      category: 'نظارت مقیم',
      desc: 'ثبت منظم شرایط جوی، نیروی انسانی، ماشین‌آلات فعال و رویدادهای کارگاهی با قابلیت خروجی استاندارد.',
      icon: BookOpen,
      theme: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
    },
    {
      id: 'focus',
      title: 'مدیریت شیفت و تایمر نظارت',
      slug: '/focus',
      category: 'بهره‌وری میدانی',
      desc: 'زمان‌بندی دوره‌های بتن‌ریزی، نمونه‌گیری‌های آزمایشگاهی و پایش فازهای اجرایی شیفت‌های کاری.',
      icon: Clock,
      theme: 'text-purple-400 bg-purple-500/10 border-purple-500/20'
    },
    {
      id: 'matrix',
      title: 'ماتریس تصمیم‌گیری مهندسی',
      slug: '/matrix',
      category: 'مدیریت فنی',
      desc: 'اولویت‌بندی چالش‌های اجرایی پروژه و انتخاب بهینه‌ترین گزینه‌های فنی بر پایه وزن‌دهی معیارها.',
      icon: Layers,
      theme: 'text-rose-400 bg-rose-500/10 border-rose-500/20'
    }
  ];

  return (
    <div className="w-full flex flex-col font-sans selection:bg-sky-500 selection:text-white">
      <SEO 
        title="جعبه‌ابزار مهندسی عمران، محاسبات کارگاهی و پایش سازه"
        description="سامانه تحت وب Civil Nexus شامل ابزارهای کاربردی مهندسی عمران: محاسبه متره و میلگرد، پایش تصویری ترک بتن، ثبت گزارش کارگاه و بایگانی اسناد فنی."
        path="/"
      />

      {/* بنر معرفی ساده و رسمی */}
      <section className="mb-6 bg-gradient-to-b from-[#0f172a] to-[#0b1220] border border-slate-800 rounded-2xl p-5 sm:p-7 shadow-lg">
        <div className="flex items-center gap-2 text-sky-400 text-xs font-semibold mb-2">
          <Sparkles className="w-4 h-4" />
          <span>پلتفرم تخصصی مهندسی عمران و پایش زیرساخت</span>
        </div>
        
        <h1 className="text-xl sm:text-2xl font-black text-white leading-snug mb-2.5">
          میزکار دیجیتال مهندسان، ناظران و کارشناسان کارگاه
        </h1>

        <p className="text-xs sm:text-sm text-slate-300 leading-relaxed max-w-2xl mb-4">
          ابزارهای فوری محاسباتی، پردازش عوارض سازه‌ای و مستندسازی میدانی بدون نیاز به نصب نرم‌افزارهای سنگین یا پیچیدگی‌های ثبت‌نام.
        </p>

        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-400 pt-3 border-t border-slate-800/80">
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> دسترسی رایگان و فوری</span>
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> سازگار با موبایل و تبلت</span>
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> کاربری آفلاین (PWA)</span>
        </div>
      </section>

      {/* عناوین و شبکه صفحات مستقل */}
      <section aria-labelledby="tools-heading">
        <div className="flex items-center justify-between mb-3 px-1">
          <h2 id="tools-heading" className="text-sm font-bold text-slate-200 flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-sky-400" />
            ابزارهای اجرایی و بخش‌های تخصصی
          </h2>
          <span className="text-[11px] text-slate-500 font-mono">۶ ابزار فعال</span>
        </div>

        <nav className="grid grid-cols-1 sm:grid-cols-2 gap-3" aria-label="ابزارهای مهندسی">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <Link
                key={tool.id}
                to={tool.slug}
                className="bg-[#0e1626] border border-slate-800 hover:border-sky-500/60 active:border-sky-500 rounded-xl p-3.5 flex flex-col justify-between transition-all group shadow-sm hover:shadow-sky-950/20"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${tool.theme}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-slate-400 font-medium">
                      {tool.category}
                    </span>
                  </div>

                  <h3 className="text-xs sm:text-sm font-bold text-slate-100 group-hover:text-sky-300 transition-colors mb-1.5">
                    {tool.title}
                  </h3>

                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    {tool.desc}
                  </p>
                </div>

                <div className="mt-3.5 pt-2.5 border-t border-slate-800/80 flex items-center justify-between text-xs text-sky-400 font-medium">
                  <span>ورود به سامانه</span>
                  <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </nav>
      </section>

      {/* فوتر ساختاریافته به سبک وب‌سایت معتبر */}
      <footer className="mt-10 pt-6 pb-2 border-t border-slate-800/80 text-slate-400 text-xs flex flex-col gap-4">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-2">
            <HardHat className="w-4 h-4 text-sky-400" />
            <span className="font-bold text-slate-200">Civil Nexus</span>
            <span className="text-[10px] text-slate-500">| پلتفرم هوشمند مهندسی عمران</span>
          </div>
          <div className="flex gap-4 text-[11px] text-slate-400">
            <Link to="/civil" className="hover:text-sky-300">محاسبات</Link>
            <Link to="/vision" className="hover:text-sky-300">پایش سازه</Link>
            <Link to="/vault" className="hover:text-sky-300">آرشیو اسناد</Link>
            <Link to="/journal" className="hover:text-sky-300">گزارش روزانه</Link>
          </div>
        </div>
        <p className="text-[10px] text-slate-500 leading-relaxed">
          طراحی‌شده برای سهولت کار مهندسان مشاور، ناظران مقیم و پیمانکاران ساختمانی و راه‌سازی. تمامی پردازش‌های داده به شکل مستقیم و ایمن در دستگاه کاربر انجام می‌شود.
        </p>
      </footer>

    </div>
  );
};

export default LandingPage;
